import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, Download, FileSpreadsheet, Loader2, Play, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { fetchSummary, uploadWorkbook } from './planningApi';

const API = process.env.REACT_APP_BACKEND_URL;

export const PlannerCard = ({ subject, yearLevel, isAdmin, onSummary }) => {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const reqId = useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    return fetchSummary(subject, yearLevel).then((d) => { if (id === reqId.current) { setSummary(d); onSummary?.(d); } });
  }, [subject, yearLevel, onSummary]);
  useEffect(() => { setSummary(null); load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (summary?.workbook && !window.confirm('Replace the current planning workbook? Teachers\' tick-offs for the old one will be removed.')) return;
    setBusy(true);
    try {
      const r = await uploadWorkbook(file, subject, yearLevel);
      toast.success(`Planner loaded: ${r.weeks} weeks, ${r.lessons} lessons`);
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const wb = summary?.workbook;
  const open = (tab) => navigate(`/dashboard/planner?subject=${encodeURIComponent(subject)}&year=${yearLevel}&tab=${tab}`);

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 space-y-4" data-testid="planner-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-amber-500 p-2 text-white"><CalendarRange className="h-5 w-5" /></div>
          <div>
            <h3 className="font-semibold text-slate-900">Interactive Planner</h3>
            <p className="text-sm text-slate-600">Year Planner, Scheme of Work and Daily Lessons as live pages — tick lessons as you teach and progress updates automatically.</p>
          </div>
        </div>
        {isAdmin && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onPick} data-testid="planner-upload-input" />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="planner-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{wb ? 'Replace workbook' : 'Upload planning workbook (Excel)'}
            </Button>
          </>
        )}
      </div>

      {summary === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
      ) : wb ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-700"><FileSpreadsheet className="h-4 w-4 text-emerald-600" /><span className="font-medium" data-testid="planner-card-title">{wb.title}</span><span className="text-slate-400">· {wb.weeks} weeks · {wb.lessons} lessons</span></div>
          <div>
            <div className="flex justify-between text-xs text-slate-500 mb-1"><span>My progress</span><span className="font-semibold text-emerald-700" data-testid="planner-card-percent">{summary.progress.percent}% taught</span></div>
            <Progress value={summary.progress.percent} className="h-2" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => open('lessons')} data-testid="planner-open-lessons"><Play className="h-4 w-4 mr-1" />Today's lesson</Button>
            <Button size="sm" variant="outline" onClick={() => open('year')} data-testid="planner-open-year">1 · Year Planner</Button>
            <Button size="sm" variant="outline" onClick={() => open('scheme')} data-testid="planner-open-scheme">2 · Scheme of Work</Button>
            {wb.has_strategies && <Button size="sm" variant="outline" onClick={() => open('strategies')} data-testid="planner-open-strategies">4 · Teaching Strategies</Button>}
            {wb.has_assessment_plan && <Button size="sm" variant="outline" onClick={() => open('assessment')} data-testid="planner-open-assessment">5 · Assessment Plan</Button>}
            <Button size="sm" variant="outline" onClick={() => open('progress')} data-testid="planner-open-progress">Progress</Button>
            {wb.resource_id && (
              <Button asChild size="sm" variant="ghost" data-testid="planner-download-excel">
                <a href={`${API}/api/resource-library/files/${wb.resource_id}?token=${token}&download=1`}><Download className="h-4 w-4 mr-1" />Excel</a>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic" data-testid="planner-card-empty">No planning workbook uploaded yet{isAdmin ? ' — upload the Excel planning pack above.' : '.'}</p>
      )}
    </div>
  );
};
